import React from 'react';
import type { BoxProps } from '@packages/shared/src/components/Box';
import { Box } from '@packages/shared/src/components/Box';

export type HorizontalScrollProps = {
  /**
   * the children of the scroll wrapper
   */
  children: React.ReactNode;
  /**
   * the offset left and right to give the items some space to the container
   *
   * @default 0
   */
  offset?: string | number;
  /**
   * a flex gap value to give the flex items some space
   *
   * @default 0
   */
  gap?: string | number;
  /**
   * initial scroll to an item with the given index (starting with 0)
   *
   * @default 0
   */
  scrollToItemIndex?: number;
  /**
   * a data attribute for the active item
   * the value will be set to true if the item is active
   * the value will be set to false if the item is inactive
   */
  activeDataAttribute?: string;
  sx?: BoxProps['sx'];
};

export type HorizontalScrollHandle = {
  /**
   * handler function to scroll to a given index starting at 0
   */
  scrollToIndex: (index: number) => void;
  /**
   * handler function to evaluate if the scroller has overflowing content
   *
   * can be used in combination with window.resize to toggle a showAll button for example
   */
  hasScrollContent: () => boolean;
};

function scrollToItem(
  index: number,
  wrapperRef: HTMLDivElement,
  activeDataAttribute: HorizontalScrollProps['activeDataAttribute'],
) {
  if (typeof wrapperRef.scrollTo === 'function') {
    const scrollItem = wrapperRef.children.item(index);

    if (scrollItem) {
      // needs to be calulated here because offset prop can be every unit of measurement and we need pixels here
      const paddingWrapper = parseFloat(window.getComputedStyle(wrapperRef).paddingLeft);
      // reset active
      if (activeDataAttribute !== undefined) {
        const items = wrapperRef.querySelectorAll(`[${activeDataAttribute}]`);
        items.forEach((item) => {
          item.setAttribute(activeDataAttribute, 'false');
        });
        scrollItem.setAttribute(activeDataAttribute, 'true');
      }
      // calculate offset left
      const offsetLeft = (scrollItem as HTMLElement).offsetLeft - paddingWrapper;
      wrapperRef.scrollTo({ left: offsetLeft });
    }
  }
}

const HorizontalScrollComponent: React.ForwardRefRenderFunction<
  HorizontalScrollHandle,
  HorizontalScrollProps
> = ({ offset = 0, gap = 0, scrollToItemIndex = 0, activeDataAttribute, children, sx }, ref) => {
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (wrapperRef?.current !== null && scrollToItemIndex > 0) {
      scrollToItem(scrollToItemIndex, wrapperRef.current, activeDataAttribute);
    }
  }, [scrollToItemIndex, wrapperRef, activeDataAttribute]);

  React.useImperativeHandle(ref, () => ({
    scrollToIndex(index: number) {
      if (wrapperRef.current !== null) {
        scrollToItem(index, wrapperRef.current, activeDataAttribute);
      }
    },
    hasScrollContent(): boolean {
      return wrapperRef?.current
        ? wrapperRef.current.clientWidth < wrapperRef.current.scrollWidth
        : false;
    },
  }));

  return (
    <Box
      ref={wrapperRef}
      sx={{
        ...{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          py: 0,
          px: offset,
          gap,

          // hide scrollbar
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          scrollBehavior: 'smooth',
          userSelect: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },

          // overflow behavior
          overflowX: 'auto',
          overscrollBehavior: 'contain',

          // scroll snapping
          scrollSnapType: 'inline mandatory',
          scrollPaddingInline: offset,
          '& > *': {
            scrollSnapAlign: 'start',
          },

          /* is needed to get correct offsetLeft value */
          position: 'relative',
        },
        ...sx,
      }}
    >
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          const props: Record<string, any> =
            typeof child.props === 'object' ? { ...child.props } : {};
          if (activeDataAttribute !== undefined) {
            props[activeDataAttribute] = `${index === 0}`;
          }
          return React.cloneElement(child, props);
        }
        return null;
      })}
    </Box>
  );
};

export const HorizontalScroll = React.forwardRef(HorizontalScrollComponent);
